import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { Container, Stack, Text, Title, Group, Badge, Button, Paper, Loader, Center, Progress, Box, ActionIcon } from '@mantine/core'
import { IconPlayerPlay, IconUsers, IconCheck, IconDownload, IconFileTypePdf, IconFileTypeDocx, IconFileText } from '@tabler/icons-react'
import { storyService } from '../services/storyService'
import { playerSessionService } from '../services/playerSessionService'
import { isAuthenticatedAtom } from '../atoms/authAtoms'
import { useTeams } from '../hooks/useTeam'
import { MarkdownRenderer } from '../components/common/MarkdownRenderer'

export default function StoryDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [isAuthenticated] = useAtom(isAuthenticatedAtom)

  const { data: story, isLoading } = useQuery({
    queryKey: ['story', id],
    queryFn: () => storyService.getStory(id),
    enabled: !!id,
  })

  // If a team the player is on has THIS story active, nudge them toward the team
  // door — this page only ever launches a SOLO session, so without the nudge a
  // student could unknowingly start solo when their cohort is playing together.
  const { data: myTeams = [] } = useTeams({ enabled: isAuthenticated })
  const teamPlayingThis = myTeams.find(
    (t) => t.current_story_id && (t.current_story_id === id || t.current_story_id === story?.id),
  )

  const { data: threadsResponse } = useQuery({
    queryKey: ['storyThreads', id],
    queryFn: () => storyService.getStoryThreads(id, { limit: 100 }),
    enabled: !!id,
  })

  // Fetch existing session to show progress (only for authenticated users).
  // READ-ONLY: this page must never CREATE a session — using startOrResumeSession
  // here (a POST) spawned a stray solo session for every authenticated visit,
  // leaving students with a duplicate beat-0 solo session alongside their real
  // team play. getInProgressSessionForStory is a plain GET that only reads.
  // No staleTime — always refetch so we pick up responses recorded during gameplay.
  // When this story is the player's active TEAM story, scope the progress read
  // to that team_story so we show the team session — not a stray solo session
  // for the same story, which would render as "back at thread 1, later threads
  // grayed out". teamPlayingThis carries current_team_story_id from the summary.
  const teamCtx = teamPlayingThis?.current_team_story_id
    ? { teamStoryId: teamPlayingThis.current_team_story_id }
    : null
  const { data: session } = useQuery({
    queryKey: ['playerSessionProgress', id, teamCtx?.teamStoryId ?? null],
    queryFn: () => playerSessionService.getInProgressSessionForStory(id, teamCtx),
    enabled: !!id && isAuthenticated,
    retry: 1,
  })

  const threads = (threadsResponse?.data || []).filter((t) => t.active !== false).sort((a, b) => a.position - b.position)

  // Compute per-thread progress from session position data
  // Position is updated on every beat navigation, unlike responses which only
  // exist for interactive beats (decision/input)
  const sessionThreadIndex = session?.current_thread_index ?? -1
  const sessionBeatIndex = session?.current_beat_index ?? 0
  const threadProgress = threads.map((thread, idx) => {
    const totalBeats = thread.beats_count || 0
    if (totalBeats === 0) return { thread, visited: 0, total: 0, percent: 0 }

    if (idx < sessionThreadIndex) {
      // Already passed this thread
      return { thread, visited: totalBeats, total: totalBeats, percent: 100 }
    }
    if (idx === sessionThreadIndex) {
      // Currently in this thread — use beat index for progress
      const visited = Math.min(sessionBeatIndex + 1, totalBeats)
      return { thread, visited, total: totalBeats, percent: (visited / totalBeats) * 100 }
    }
    // Haven't reached this thread yet
    return { thread, visited: 0, total: totalBeats, percent: 0 }
  })

  const hasProgress = session && (sessionThreadIndex > 0 || sessionBeatIndex > 0)

  if (isLoading) {
    return (
      <Center py="xl" style={{ minHeight: '60vh' }}>
        <Loader size="lg" color="cyan" />
      </Center>
    )
  }

  if (!story) {
    return (
      <Center py="xl" style={{ minHeight: '60vh' }}>
        <Text c="dimmed">Story not found.</Text>
      </Center>
    )
  }

  const handlePlay = () => {
    if (isAuthenticated) {
      navigate(`/play/${id}`)
    } else {
      navigate(`/start/${id}`)
    }
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Stack gap="xs">
          <Title order={2} ff="Orbitron, sans-serif" c="cyan.4">
            {story.name}
          </Title>
          {story.scenario_name && (
            <Text size="lg" c="dimmed">{story.scenario_name}</Text>
          )}
        </Stack>

        <Group gap="xs">
          {story.characters_count > 0 && (
            <Badge size="md" variant="light" color="teal" leftSection={<IconUsers size={14} />}>
              {story.characters_count} characters
            </Badge>
          )}
        </Group>

        {story.description && (
          <Paper p="lg" className="glass">
            <MarkdownRenderer content={story.description} />
          </Paper>
        )}

        {story?.player_documents_count > 0 && (
          <StoryDocumentsSection storyId={story.id} />
        )}

        {threads.length > 0 && (
          <Paper p="lg" className="glass">
            <Text fw={600} size="sm" c="cyan.3" mb="sm" ff="Orbitron, sans-serif">
              Threads
            </Text>
            <Stack gap="sm">
              {threadProgress.map(({ thread, visited, total, percent }) => (
                <Box key={thread.id}>
                  <Group gap="sm" mb={4}>
                    <Text size="xs" c="dimmed" w={20} ta="right">{thread.position}.</Text>
                    <Text size="sm" style={{ flex: 1 }}>{thread.name}</Text>
                    {hasProgress && percent >= 100 && (
                      <IconCheck size={14} color="var(--mantine-color-green-5)" />
                    )}
                    {hasProgress && (
                      <Text size="xs" c="dimmed">
                        {visited}/{total}
                      </Text>
                    )}
                  </Group>
                  {hasProgress && (
                    <Progress
                      value={percent}
                      size="xs"
                      color={percent >= 100 ? 'green' : 'cyan'}
                      ml={32}
                      style={{ opacity: 0.7 }}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {teamPlayingThis ? (
          // The player's team is playing THIS story. Make team play the hero and
          // demote solo to a small secondary link, so they don't start a stray
          // solo playthrough that won't count toward Team Analysis.
          <Stack gap="sm" align="center">
            <Paper
              p="lg"
              radius="md"
              w="100%"
              maw={520}
              style={{
                background: 'rgba(0, 180, 160, 0.10)',
                border: '1px solid var(--mantine-color-teal-7)',
              }}
            >
              <Stack gap="md" align="center">
                <Group gap="xs">
                  <IconUsers size={20} color="var(--mantine-color-teal-4)" />
                  <Text size="sm" ta="center">
                    Your team <Text span fw={600} c="teal.3">{teamPlayingThis.name}</Text> is playing this story. Play together so your work counts toward Team Analysis.
                  </Text>
                </Group>
                <Button
                  size="lg"
                  color="teal"
                  leftSection={<IconUsers size={20} />}
                  onClick={() => navigate(`/teams/${teamPlayingThis.id}`)}
                >
                  Play with your team
                </Button>
              </Stack>
            </Paper>
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              onClick={handlePlay}
            >
              Or start a separate solo playthrough (won't count for your team)
            </Button>
          </Stack>
        ) : (
          <Stack gap={4} align="center">
            <Button
              size="lg"
              leftSection={<IconPlayerPlay size={20} />}
              onClick={handlePlay}
              className="glow-cyan"
            >
              {hasProgress ? 'Continue Story' : 'Play Story'}
            </Button>
            {isAuthenticated && (
              <Text size="xs" c="dimmed">
                Solo play — just you. To play with your team, open it from My Team.
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  )
}

function fileTypeIcon(contentType) {
  if (contentType?.includes('pdf')) return IconFileTypePdf
  if (contentType?.includes('wordprocessingml')) return IconFileTypeDocx
  return IconFileText
}

function StoryDocumentsSection({ storyId }) {
  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['storyDocuments', storyId],
    queryFn: () => storyService.getStoryDocuments(storyId),
    enabled: !!storyId,
  })

  if (isLoading) {
    return (
      <Paper p="lg" className="glass">
        <Text fw={600} size="sm" c="cyan.3" mb="sm" ff="Orbitron, sans-serif">
          Documents
        </Text>
        <Group gap="sm">
          <Loader size="xs" color="cyan" />
          <Text size="sm" c="dimmed">Loading...</Text>
        </Group>
      </Paper>
    )
  }

  if (documents.length === 0) return null

  return (
    <Paper p="lg" className="glass">
      <Text fw={600} size="sm" c="cyan.3" mb="sm" ff="Orbitron, sans-serif">
        Documents
      </Text>
      <Stack gap="sm">
        {documents.map((doc) => {
          const FileIcon = fileTypeIcon(doc.file_content_type)
          return (
            <Group key={doc.id} gap="sm" justify="space-between">
              <Group gap="xs" style={{ flex: 1 }}>
                <FileIcon size={16} color="rgba(0, 229, 255, 0.6)" />
                <Text size="sm" style={{ flex: 1 }}>{doc.name}</Text>
              </Group>
              {doc.file_url && (
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="cyan"
                  component="a"
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Download"
                >
                  <IconDownload size={14} />
                </ActionIcon>
              )}
            </Group>
          )
        })}
      </Stack>
    </Paper>
  )
}
